/**
 * MONSTER TOWN — BUILD DEMOS + TEST SURFACE. OWNED BY THE BUILD CREW.
 *
 * Two jobs:
 *  1. Named layouts the screenshot/QA tools can drop into the world, so the
 *     auto-connect claim (LAW #5) is something you can LOOK AT rather than
 *     something a comment asserts. `npm run shot -- --demo house`.
 *  2. `window.__build`, a small deterministic surface the drive script uses to
 *     exercise the real gesture path (taps go through the canvas rect, never a
 *     synthetic shortcut) without adding methods to the frozen harness API.
 */

import { bus } from '../core/events'
import type { Cell, Rot } from '../core/grid'
import type { PaletteName } from '../core/palette'
import { world } from '../core/scene'
import type { InstanceId, PlaceableId, SceneId } from '../core/types'
import type { GameContext } from '../app/context'
import { worldToCanvasPx } from '../input/pick'
import { registerAngle, registerDemo, settle } from '../harness'
import { paletteContents, paletteLayout } from './palette'
import { plotRegion } from './plot'
import { placeAll, undo, undoDepth } from './placer'
import {
  buildTool,
  rotateSelection,
  selectPlaceable,
  selectedPlaceable,
  selectionRot,
  setBuildTool,
} from './state'

interface Item {
  defId: PlaceableId
  anchor: Cell
  rot?: Rot
  tint?: PaletteName
}

const ID = {
  wall: 'wall_plaster' as PlaceableId,
  door: 'door_wood' as PlaceableId,
  window: 'window_square' as PlaceableId,
  roof: 'roof_tile' as PlaceableId,
  floorWood: 'floor_wood' as PlaceableId,
  floorTile: 'floor_tile' as PlaceableId,
  pathStone: 'path_stone' as PlaceableId,
  pathBrick: 'path_brick' as PlaceableId,
  fence: 'fence_picket' as PlaceableId,
  stair: 'stair_wood' as PlaceableId,
  block: 'block_wood' as PlaceableId,
  flower: 'plant_flower' as PlaceableId,
}

/**
 * A whole cottage, laid out with nothing but the kit a kid has on day one.
 * The point of this layout is that NOTHING in it is bespoke geometry: every
 * corner, every eave and every fence joint is produced by the connect masks.
 */
export function houseLayout(width: number, depth: number): Item[] {
  const cx = Math.floor(width / 2)
  const cz = Math.floor(depth / 2)
  const x0 = cx - 3
  const x1 = cx + 1
  const z0 = cz - 3
  const z1 = cz
  const out: Item[] = []
  const at = (gx: number, gz: number, gy = 0): Cell => ({ gx, gy, gz })

  // floor inside the shell
  for (let x = x0 + 1; x <= x1 - 1; x++) {
    for (let z = z0 + 1; z <= z1 - 1; z++) out.push({ defId: ID.floorWood, anchor: at(x, z) })
  }

  // walls: north and south runs, with a door and two windows in them
  for (let x = x0; x <= x1; x++) {
    const north = x === x0 + 1 || x === x1 - 1 ? ID.window : ID.wall
    out.push({ defId: north, anchor: at(x, z0), tint: 'wallMint' })
    const south = x === x0 + 2 ? ID.door : ID.wall
    out.push({ defId: south, anchor: at(x, z1), tint: south === ID.door ? 'roofTeal' : 'wallMint' })
  }
  for (let z = z0 + 1; z <= z1 - 1; z++) {
    out.push({ defId: ID.wall, anchor: at(x0, z), tint: 'wallMint' })
    out.push({ defId: ID.wall, anchor: at(x1, z), tint: 'wallMint' })
  }

  // roof: a full hipped cap. Level 2 is the top of a 2-level wall.
  for (let x = x0; x <= x1; x++) {
    for (let z = z0; z <= z1; z++) out.push({ defId: ID.roof, anchor: at(x, z, 2), tint: 'roofRed' })
  }

  // Path from the door out to the front gate, then a small brick patio beside
  // it. Two DIFFERENT path defs that still join into one continuous run,
  // because they share the `path` connect group rather than knowing each other.
  const doorX = x0 + 2
  for (let z = z1 + 1; z <= z1 + 3; z++) out.push({ defId: ID.pathStone, anchor: at(doorX, z) })
  out.push({ defId: ID.pathStone, anchor: at(doorX - 1, z1 + 3) })
  out.push({ defId: ID.pathStone, anchor: at(doorX + 1, z1 + 3) })
  for (let z = z1 + 2; z <= z1 + 3; z++) {
    out.push({ defId: ID.pathBrick, anchor: at(doorX - 2, z), tint: 'dirt' })
  }

  // fence around the front yard, with a gap where the path walks through
  const fz = z1 + 4
  for (let x = x0 - 1; x <= x1 + 1; x++) {
    if (x === doorX) continue
    out.push({ defId: ID.fence, anchor: at(x, fz), tint: 'wallCream' })
  }
  for (let z = z1 + 1; z < fz; z++) {
    out.push({ defId: ID.fence, anchor: at(x0 - 1, z), tint: 'wallCream' })
    out.push({ defId: ID.fence, anchor: at(x1 + 1, z), tint: 'wallCream' })
  }

  // a garden, a stack of blocks and some steps up onto them
  out.push({ defId: ID.flower, anchor: at(x0, z1 + 2), tint: 'flowerPink' })
  out.push({ defId: ID.flower, anchor: at(x1, z1 + 2), tint: 'flowerYellow' })
  out.push({ defId: ID.block, anchor: at(x1 + 3, z1 - 1) })
  out.push({ defId: ID.block, anchor: at(x1 + 3, z1 - 1, 1) })
  out.push({ defId: ID.block, anchor: at(x1 + 3, z1) })
  out.push({ defId: ID.stair, anchor: at(x1 + 3, z1 + 1), rot: 2 })
  out.push({ defId: ID.floorTile, anchor: at(x1 + 2, z1 + 1) })
  out.push({ defId: ID.floorTile, anchor: at(x1 + 2, z1 + 2) })

  return out
}

// ---------------------------------------------------------------------------
// harness registration
// ---------------------------------------------------------------------------

let lastGhost: { anchor: Cell; rot: Rot; valid: boolean } | null = null
let demoSceneId: SceneId | null = null

/**
 * The biggest outdoor plot in the game. Resolved by SIZE, not by name or by
 * whatever happens to be active — the world crew owns the scene list and may
 * add, rename or reorder scenes without breaking these captures.
 */
function biggestExterior(): SceneId | null {
  let bestId: SceneId | null = null
  let bestArea = 0
  for (const id of world.ids()) {
    const node = world.require(id)
    if (node.def.kind !== 'exterior') continue
    const area = node.grid.spec.width * node.grid.spec.depth
    if (area > bestArea) {
      bestArea = area
      bestId = id
    }
  }
  return bestId
}

export function installBuildDemos(ctx: GameContext): void {
  installBuildTestApi(ctx)
  bus.on('build:ghostMoved', (e) => {
    lastGhost = { anchor: e.anchor, rot: e.rot, valid: e.valid }
  })

  // Resolve the plot on a microtask, not here: crews that install AFTER the
  // build crew (venue, interiors) also register scenes, and the demo and its
  // camera angles must agree on which one is "the big outdoor plot".
  queueMicrotask(() => {
    const sceneId = biggestExterior()
    if (sceneId === null) return
    demoSceneId = sceneId
    const grid = world.require(sceneId).grid
    const centre = grid.cellToWorld({
      gx: Math.floor(grid.spec.width / 2) - 1,
      gy: 0,
      gz: Math.floor(grid.spec.depth / 2),
    })
    // Two angles only, on purpose: every registered angle costs the QA drive a
    // scene switch and three settled frames, and that step is already close to
    // its timeout now that six crews have added their own.
    registerAngle({ name: 'build-house', sceneId, target: centre, rot: 0, zoom: 1 })
    registerAngle({ name: 'build-house-side', sceneId, target: centre, rot: 1, zoom: 1 })
  })

  // NOTE: namespaced. Demo names are a flat global map and the interiors crew
  // also registers a demo called 'house'; last install wins, so build-crew
  // layouts are prefixed to stay ours.
  registerDemo('kit-house', async () => {
    const id = demoSceneId ?? biggestExterior()
    if (id === null) return 0
    if (world.activeSceneId !== id) world.setActiveImmediate(id)
    const grid = world.require(id).grid
    const items = houseLayout(grid.spec.width, grid.spec.depth)
    placeAll(id, items)
    await settle(2)
    return grid.count()
  })
}

// ---------------------------------------------------------------------------
// window.__build — deterministic test surface for tools/drive.mjs
// ---------------------------------------------------------------------------

export interface BuildTestApi {
  /** Tap at canvas CSS pixels, through the real touch router and canvas rect. */
  tapAt(x: number, y: number): void
  /** Tap the middle of a grid cell — same path, resolved through the camera. */
  tapCell(gx: number, gz: number): void
  /** Canvas CSS pixel of a cell centre, for driving real pointer gestures. */
  cellToPx(gx: number, gz: number): { x: number; y: number } | null
  /** Which cell an instance currently occupies, for asserting a slide. */
  cellOf(instanceId: string): { gx: number; gy: number; gz: number } | null
  /** The instance in a cell, or null. */
  idAt(gx: number, gy: number, gz: number): string | null
  /** The live auto-connect mask of whatever is in a cell. -1 = nothing/no group. */
  connectAt(gx: number, gy: number, gz: number): number
  select(defId: string): void
  selected(): string | null
  rotate(): void
  rot(): Rot
  tool(t: 'place' | 'delete'): void
  currentTool(): string
  undo(): boolean
  undoDepth(): number
  palette(): string[]
  /** Proof that nothing in the drawer is unreachable: `offscreen` must be 0. */
  paletteLayout(): {
    tab: string
    page: number
    pages: number
    perPage: number
    total: number
    rendered: number
    offscreen: number
  }
  ghost(): { anchor: Cell; rot: Rot; valid: boolean } | null
  /** The buildable plot of the active scene, in cells. QA reads this. */
  plotRegion(): { gx0: number; gz0: number; gx1: number; gz1: number; scene: string } | null
  /** The active scene's grid, for asserting that a room fits on screen. */
  gridSpec(): { id: string; kind: string; w: number; d: number; levels: number } | null
  count(sceneId?: string): number
  /** All placements in a scene, for asserting an exact save round trip. */
  dump(sceneId?: string): { id: string; def: string; x: number; y: number; z: number; r: Rot }[]
  paletteVisible(): boolean
  /** Which plot the `house` demo and the build-* angles agree on. */
  demoScene(): string | null
  scenes(): { id: string; kind: string; w: number; d: number; levels: number; count: number }[]
}

declare global {
  interface Window {
    __build?: BuildTestApi
  }
}

export function installBuildTestApi(ctx: GameContext): void {
  const activeGridOf = (sceneId?: string) => {
    const id = (sceneId ?? world.activeSceneId) as SceneId | null
    return id === null ? null : (world.get(id)?.grid ?? null)
  }

  window.__build = {
    tapAt: (x, y) => ctx.touch.simulateTapAtCanvasPx(x, y),
    tapCell: (gx, gz) => {
      const id = world.activeSceneId
      if (id === null) return
      const node = world.require(id)
      const w = node.grid.cellToWorld({ gx, gy: 0, gz })
      const p = worldToCanvasPx(ctx.camera.camera, ctx.canvas, w)
      ctx.touch.simulateTapAtCanvasPx(p.x, p.y)
    },
    cellToPx: (gx, gz) => {
      const id = world.activeSceneId
      if (id === null) return null
      const node = world.require(id)
      const w = node.grid.cellToWorld({ gx, gy: 0, gz })
      const p = worldToCanvasPx(ctx.camera.camera, ctx.canvas, w)
      return { x: p.x, y: p.y }
    },
    cellOf: (instanceId) => {
      const id = world.activeSceneId
      if (id === null) return null
      const p = world.require(id).grid.placement(instanceId as InstanceId)
      return p === null ? null : { gx: p.anchor.gx, gy: p.anchor.gy, gz: p.anchor.gz }
    },
    idAt: (gx, gy, gz) => {
      const id = world.activeSceneId
      if (id === null) return null
      return (world.require(id).grid.at({ gx, gy, gz }) as string | null) ?? null
    },
    connectAt: (gx, gy, gz) => {
      const id = world.activeSceneId
      if (id === null) return -1
      const node = world.require(id)
      const p = node.grid.placementAt({ gx, gy, gz })
      if (p === null || p.connectGroup === null) return -1
      return node.grid.connectMask({ gx, gy, gz }, p.connectGroup)
    },
    select: (defId) => selectPlaceable(defId as PlaceableId),
    selected: () => selectedPlaceable(),
    rotate: () => rotateSelection(),
    rot: () => selectionRot(),
    tool: (t) => setBuildTool(t),
    currentTool: () => buildTool(),
    undo: () => undo(),
    undoDepth: () => undoDepth(),
    palette: () => paletteContents(),
    paletteLayout: () => paletteLayout(),
    ghost: () => lastGhost,
    plotRegion: () => plotRegion(),
    gridSpec: () => {
      const id = world.activeSceneId
      if (id === null) return null
      const n = world.get(id)
      if (n === undefined) return null
      return { id: id as string, kind: n.def.kind, w: n.grid.spec.width, d: n.grid.spec.depth, levels: n.grid.spec.levels }
    },
    count: (sceneId) => activeGridOf(sceneId)?.count() ?? 0,
    dump: (sceneId) =>
      (activeGridOf(sceneId)?.all() ?? []).map((p) => ({
        id: p.instanceId as string,
        def: p.defId as string,
        x: p.anchor.gx,
        y: p.anchor.gy,
        z: p.anchor.gz,
        r: p.rot,
      })),
    paletteVisible: () => document.getElementById('mt-build')?.dataset['visible'] === 'true',
    demoScene: () => demoSceneId,
    scenes: () =>
      world.ids().map((id) => {
        const n = world.require(id)
        return {
          id: id as string,
          kind: n.def.kind,
          w: n.grid.spec.width,
          d: n.grid.spec.depth,
          levels: n.grid.spec.levels,
          count: n.grid.count(),
        }
      }),
  }
}
